<template>
  <MobileAuthLayout>
    <template #default="{ facilityId }">
      {{ updateFacilityId(facilityId) }}

      <div class="mobile-overlock-content">
        <div v-if="currentScreen === 'manage'" class="screen-container">
          <div class="screen-header">
            <h2>Manage Overlocks</h2>
            <p>Select units to apply overlock protection</p>
          </div>

          <div class="table-header">
            <div class="header-applied">APPLIED</div>
            <div class="header-unit">UNIT #</div>
            <div class="header-comment">COMMENT</div>
          </div>

          <div class="units-list">
            <div v-for="unit in units" :key="unit.id" class="unit-row">
              <div class="unit-checkbox">
                <v-checkbox
                  :input-value="isUnitSelected(unit.id)"
                  @change="toggleUnit(unit.id, $event)"
                  color="primary"
                  hide-details
                  dense
                />
              </div>

              <div class="unit-number">
                {{ unit.unitNumber }}
              </div>

              <div class="unit-comment">
                <v-text-field
                  :value="getUnitComment(unit.id)"
                  @input="updateComment(unit.id, $event)"
                  placeholder="Add..."
                  dense
                  outlined
                  hide-details
                  :disabled="!isUnitSelected(unit.id)"
                  class="comment-field"
                />
              </div>
            </div>
          </div>

          <div class="action-section">
            <v-btn
              color="success"
              large
              block
              :disabled="selectedUnits.length === 0"
              :loading="applyingOverlocks"
              @click="applyOverlocks"
              class="apply-btn"
            >
              APPLY OVERLOCKS
            </v-btn>
          </div>
        </div>

        <div v-if="currentScreen === 'completed'" class="screen-container">
          <div class="success-screen">
            <div class="success-icon">
              <v-icon size="80" color="success">fas fa-check-circle</v-icon>
            </div>
            <h2>Task Completed</h2>
            <p class="success-message">
              Successfully applied overlocks to {{ completedCount }} unit{{
                completedCount !== 1 ? "s" : ""
              }}
            </p>

            <div class="completed-summary">
              <div
                v-for="unit in completedUnits"
                :key="unit.id"
                class="completed-item"
              >
                <div class="completed-unit">{{ unit.unitNumber }}</div>
                <div v-if="unit.comment" class="completed-comment">
                  "{{ unit.comment }}"
                </div>
              </div>
            </div>

            <div class="completion-actions">
              <v-btn
                color="primary"
                large
                block
                class="mb-3"
                @click="startNewSession"
              >
                Manage More Units
              </v-btn>

              <v-btn color="grey" outlined block @click="exitSession">
                Exit
              </v-btn>
            </div>
          </div>
        </div>

        <v-snackbar v-model="showMessage" :color="messageType" timeout="4000">
          {{ message }}
          <template #action="{ attrs }">
            <v-btn text v-bind="attrs" @click="showMessage = false">
              Close
            </v-btn>
          </template>
        </v-snackbar>
      </div>
    </template>
  </MobileAuthLayout>
</template>

<script>
import { ref, computed, onMounted } from "vue";
import MobileAuthLayout from "../../../mobile/MobileAuthLayout.vue";
import { useMobileOverlock } from "./useMobileOverlock";

export default {
  name: "PageMobileOverlockManagement",

  components: {
    MobileAuthLayout,
  },

  setup() {
    const {
      units,
      selectedUnits,
      completedUnits,
      loading,
      applyingOverlocks,
      loadUnits,
      isUnitSelected,
      toggleUnit,
      getUnitComment,
      updateComment,
      applyOverlocks: performApplyOverlocks,
      clearSession,
    } = useMobileOverlock();

    const currentScreen = ref("manage");
    const currentFacilityId = ref(null);
    const showMessage = ref(false);
    const message = ref("");
    const messageType = ref("success");

    const completedCount = computed(() => completedUnits.value.length);

    const updateFacilityId = async (facilityId) => {
      if (facilityId && facilityId !== currentFacilityId.value) {
        currentFacilityId.value = facilityId;
        await loadUnits(facilityId);
      }
    };

    const applyOverlocks = async () => {
      try {
        await performApplyOverlocks();
        currentScreen.value = "completed";
        showSuccessMessage("Overlocks applied successfully!");
      } catch (error) {
        console.error("Error applying overlocks:", error);
        showErrorMessage("Failed to apply overlocks. Please try again.");
      }
    };

    const startNewSession = () => {
      currentScreen.value = "manage";
    };

    const exitSession = () => {
      clearSession();
      window.history.back();
    };

    const showSuccessMessage = (msg) => {
      message.value = msg;
      messageType.value = "success";
      showMessage.value = true;
    };

    const showErrorMessage = (msg) => {
      message.value = msg;
      messageType.value = "error";
      showMessage.value = true;
    };

    return {
      currentScreen,
      currentFacilityId,
      units,
      selectedUnits,
      completedUnits,
      completedCount,
      loading,
      applyingOverlocks,
      showMessage,
      message,
      messageType,
      updateFacilityId,
      isUnitSelected,
      toggleUnit,
      getUnitComment,
      updateComment,
      applyOverlocks,
      startNewSession,
      exitSession,
    };
  },
};
</script>

<style scoped>
.mobile-overlock-content {
  min-height: 100vh;
  background-color: #f5f5f5;
}

.screen-container {
  padding: 20px 16px;
  max-width: 600px;
  margin: 0 auto;
  background: white;
}

.screen-header {
  text-align: center;
  margin-bottom: 24px;
}

.screen-header h2 {
  color: #1976d2;
  margin: 0 0 8px 0;
  font-size: 1.5rem;
  font-weight: 600;
}

.screen-header p {
  color: #666;
  margin: 0 0 16px 0;
  line-height: 1.4;
}

.table-header {
  display: flex;
  align-items: center;
  padding: 16px;
  background: #f8f9fa;
  border-bottom: 1px solid #e0e0e0;
  font-weight: 600;
  color: #666;
  font-size: 14px;
  margin: 0 -16px 0 -16px;
}

.header-applied {
  width: 80px;
  text-align: center;
}

.header-unit {
  width: 80px;
  text-align: center;
}

.header-comment {
  flex: 1;
  text-align: left;
  padding-left: 16px;
}

.units-list {
  max-height: 60vh;
  overflow-y: auto;
  margin: 0 -16px;
}

.unit-row {
  display: flex;
  align-items: center;
  padding: 12px 16px;
  border-bottom: 1px solid #f0f0f0;
}

.unit-row:last-child {
  border-bottom: none;
}

.unit-checkbox {
  width: 80px;
  display: flex;
  justify-content: center;
}

.unit-number {
  width: 80px;
  text-align: center;
  font-weight: 500;
  color: #1976d2;
}

.unit-comment {
  flex: 1;
  padding-left: 16px;
}

.comment-field {
  max-width: 200px;
}

.action-section {
  padding: 24px 0 0 0;
  background: white;
  border-top: 1px solid #e0e0e0;
  margin-top: 16px;
}

.apply-btn {
  border-radius: 8px !important;
  font-weight: 600 !important;
  text-transform: none !important;
  letter-spacing: 0.5px !important;
}

.success-screen {
  text-align: center;
  padding: 40px 20px;
}

.success-icon {
  margin-bottom: 20px;
}

.success-screen h2 {
  color: #4caf50;
  margin: 0 0 16px 0;
  font-size: 1.8rem;
  font-weight: 600;
}

.success-message {
  color: #666;
  font-size: 16px;
  margin-bottom: 24px;
  line-height: 1.4;
}

.completed-summary {
  background: #f8f9fa;
  border-radius: 12px;
  padding: 20px;
  margin: 24px 0;
  text-align: left;
}

.completed-item {
  padding: 12px 0;
  border-bottom: 1px solid #e0e0e0;
}

.completed-item:last-child {
  border-bottom: none;
  padding-bottom: 0;
}

.completed-unit {
  font-weight: 600;
  color: #1976d2;
  margin-bottom: 4px;
}

.completed-comment {
  color: #666;
  font-style: italic;
  font-size: 14px;
}

.completion-actions {
  margin-top: 32px;
}

@media (max-width: 768px) {
  .screen-container {
    padding: 16px 12px;
  }

  .comment-field {
    max-width: 150px;
  }

  .header-comment {
    padding-left: 8px;
  }

  .unit-comment {
    padding-left: 8px;
  }
}
</style>
